import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Form, Input, Button, Spin, message, Card, Select, Space,
  Row, Col, Tag, Table, Statistic, Divider, Tabs, InputNumber
} from "antd";
import { SaveOutlined, ArrowLeftOutlined, DollarOutlined } from "@ant-design/icons";
import { api } from "@redux/api";
import { useSelector } from "react-redux";
import { useKeepAliveContext } from "@/hooks/use-keep-alive-context";
import { EventName, emitEvent } from "@/hooks/useEventBus";
import moment from "moment";
import CustomDatePicker from "@components/CustomDatePicker";
import { buildFacilityPath, getFacilityContext } from "@/utils/route-context";

const { Option } = Select;
const { TextArea } = Input;
const { TabPane } = Tabs;

const WORKORDER_TYPE_COLORS = { PM: "green", CM: "blue", EM: "red" };
// WorkorderWrapper 自身状态颜色
const WRAPPER_STATUS_COLORS = {
  Open: "blue",
  "In Progress": "orange",
  Completed: "green",
  Cancelled: "red",
};

// 子 KpiWorkorder 状态颜色
const WORKORDER_STATUS_COLORS = {
  "Not Started": "default",
  "In Progress": "orange",
  Completed: "green",
  Cancelled: "red",
};

const WorkorderWrapperDetail = ({ isAdd = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { refreshTab } = useKeepAliveContext();
  const [form] = Form.useForm();
  const facilityId = useSelector((state) => state.Facility?.selected?.Id);
  const facilityContext = useSelector((state) => getFacilityContext(state));
  const listRoute = buildFacilityPath("workorder-wrapper", facilityContext) || "/switch-view";

  const [loading, setLoading] = useState(!isAdd);
  const [saving, setSaving] = useState(false);
  const [readOnly, setReadOnly] = useState(!isAdd);
  const [wrapperData, setWrapperData] = useState(null);
  const [workorders, setWorkorders] = useState([]);
  const [costSummary, setCostSummary] = useState(null);
  const [activeTab, setActiveTab] = useState("info");

  useEffect(() => {
    if (!isAdd && id) {
      fetchDetail();
    }
  }, [id, isAdd]);

  const fetchDetail = async () => {
    try {
      setLoading(true);
      const [detailRes, costRes] = await Promise.all([
        api.getWorkorderWrapperDetail(id),
        api.getWorkorderWrapperCostSummary(id),
      ]);

      if (detailRes.status === 200) {
        const data = detailRes.data;
        setWrapperData(data);
        setWorkorders(data.workorders || []);
        form.setFieldsValue({
          title: data.title,
          description: data.description,
          status: data.status,
          start_date: data.start_date,
          end_date: data.end_date,
          estimate_budget: data.estimate_budget,
        });
      }
      if (costRes.status === 200) {
        setCostSummary(costRes.data);
      }
    } catch {
      message.error("Failed to load workorder wrapper");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const body = { ...values, facility: facilityId };

      let res;
      if (isAdd) {
        res = await api.createWorkorderWrapper(body);
      } else {
        res = await api.editWorkorderWrapper(id, body);
      }

      if (res.status === 200 || res.status === 201) {
        message.success(isAdd ? "Workorder Wrapper created" : "Workorder Wrapper updated");
        emitEvent(EventName.REFRESH_WORKORDER_WRAPPER_LIST);
        refreshTab(listRoute);
        if (isAdd) navigate(buildFacilityPath(`workorder-wrapper-detail/${res.data.id}`, facilityContext) || listRoute);
        else { setReadOnly(true); fetchDetail(); }
      } else {
        message.error("Save failed");
      }
    } catch (err) {
      if (err.errorFields) return;
      message.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const workorderColumns = [
    {
      title: "Issue",
      dataIndex: "issue",
      key: "issue",
      render: (text, record) => (
        <a onClick={() => navigate(buildFacilityPath(`workorder-detail/${record.id}`, facilityContext) || listRoute)}>{text}</a>
      ),
    },
    {
      title: "Type",
      dataIndex: "workorder_type",
      key: "workorder_type",
      width: 70,
      render: (v) => <Tag color={WORKORDER_TYPE_COLORS[v] || "default"}>{v}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (v) => <Tag color={WORKORDER_STATUS_COLORS[v] || "default"}>{v}</Tag>,
    },
    {
      title: "Equipment",
      key: "equipment",
      render: (_, rec) => rec.equipment_name || rec.equipment || "-",
    },
    {
      title: "Est. Budget",
      dataIndex: "estimate_budget",
      key: "estimate_budget",
      width: 110,
      render: (v) => v ? `$${parseFloat(v).toFixed(2)}` : "-",
    },
    {
      title: "Total Cost",
      dataIndex: "total_cost",
      key: "total_cost",
      width: 110,
      render: (v) => v ? `$${parseFloat(v).toFixed(2)}` : "-",
    },
    {
      title: "Start Date",
      dataIndex: "start_date",
      key: "start_date",
      width: 110,
    },
  ];

  if (loading) return <div style={{ padding: 40, textAlign: "center" }}><Spin size="large" /></div>;

  return (
    <div style={{ padding: "16px" }}>
      <Card
        title={
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => { refreshTab(listRoute); navigate(listRoute); }} />
            <span style={{ fontSize: 18, fontWeight: 600 }}>
              {isAdd ? "Create Workorder Wrapper" : `Workorder Wrapper #${id}`}
            </span>
            {wrapperData?.status && (
              <Tag color={WRAPPER_STATUS_COLORS[wrapperData.status] || "default"} style={{ fontSize: 13 }}>
                {wrapperData.status}
              </Tag>
            )}
            {wrapperData?.pm_plan_name && (
              <Tag color="purple">PM: {wrapperData.pm_plan_name}</Tag>
            )}
          </Space>
        }
        extra={
          <Space>
            {!isAdd && readOnly && <Button onClick={() => setReadOnly(false)}>Edit</Button>}
            {(!readOnly || isAdd) && (
              <>
                {!isAdd && <Button onClick={() => { setReadOnly(true); fetchDetail(); }}>Cancel</Button>}
                <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
                  Save
                </Button>
              </>
            )}
          </Space>
        }
      >
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          {/* Info Tab */}
          <TabPane tab="Overview" key="info">
            <Form form={form} layout="vertical" disabled={readOnly && !isAdd}>
              <Row gutter={16}>
                <Col span={16}>
                  <Form.Item label="Title" name="title" rules={[{ required: true }]}>
                    <Input placeholder="Workorder Wrapper title" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Status" name="status" initialValue="Open">
                    <Select>
                      <Option value="Open">Open</Option>
                      <Option value="In Progress">In Progress</Option>
                      <Option value="Completed">Completed</Option>
                      <Option value="Cancelled">Cancelled</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item label="Description" name="description">
                    <TextArea rows={2} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Start Date" name="start_date">
                    <CustomDatePicker format="YYYY-MM-DD" style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="End Date" name="end_date">
                    <CustomDatePicker format="YYYY-MM-DD" style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Estimated Budget ($)" name="estimate_budget">
                    <InputNumber min={0} precision={2} style={{ width: "100%" }} placeholder="0.00" />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </TabPane>

          {/* Workorders Tab */}
          {!isAdd && (
            <TabPane tab={`Workorders (${workorders.length})`} key="workorders">
              <Table
                rowKey="id"
                columns={workorderColumns}
                dataSource={workorders}
                pagination={false}
                scroll={{ x: 800 }}
                size="small"
              />
            </TabPane>
          )}

          {/* Cost Summary Tab */}
          {!isAdd && costSummary && (
            <TabPane tab={<span><DollarOutlined /> Cost Summary</span>} key="cost">
              <Row gutter={24} style={{ marginTop: 16 }}>
                <Col span={6}>
                  <Statistic
                    title="Estimated Budget"
                    value={parseFloat(costSummary.estimate_budget || 0).toFixed(2)}
                    prefix="$"
                    valueStyle={{ color: "#3A3F63" }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Labor Cost"
                    value={parseFloat(costSummary.labor_cost || 0).toFixed(2)}
                    prefix="$"
                    valueStyle={{ color: "#2563eb" }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Parts Cost"
                    value={parseFloat(costSummary.parts_cost || 0).toFixed(2)}
                    prefix="$"
                    valueStyle={{ color: "#059669" }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Vendor Cost"
                    value={parseFloat(costSummary.vendor_cost || 0).toFixed(2)}
                    prefix="$"
                    valueStyle={{ color: "#d97706" }}
                  />
                </Col>
              </Row>
              <Divider />
              <Row gutter={24}>
                <Col span={6}>
                  <Statistic
                    title="Total Actual Cost"
                    value={parseFloat(costSummary.total_cost || 0).toFixed(2)}
                    prefix="$"
                    valueStyle={{ color: "#dc2626", fontSize: 24, fontWeight: 700 }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Budget Variance"
                    value={Math.abs(
                      parseFloat(costSummary.estimate_budget || 0) - parseFloat(costSummary.total_cost || 0)
                    ).toFixed(2)}
                    prefix={
                      parseFloat(costSummary.total_cost || 0) > parseFloat(costSummary.estimate_budget || 0)
                        ? "Over $"
                        : "Under $"
                    }
                    valueStyle={{
                      color: parseFloat(costSummary.total_cost || 0) > parseFloat(costSummary.estimate_budget || 0)
                        ? "#dc2626"
                        : "#059669",
                    }}
                  />
                </Col>
              </Row>
            </TabPane>
          )}
        </Tabs>
      </Card>
    </div>
  );
};

export default WorkorderWrapperDetail;
